import {
  Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  VIEW_ENTITY_TYPES,
  getQuotesOverviewQueryKeyPrefix,
  QuotesOverviewRow,
  useGetQuotesOverview,
  type QuotesOverviewResponse,
} from '@frontend/api';
import {
  TableContainer,
  TableV2,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Button,
  Tooltip,
  OutlineCircleInfoIcon,
  Box,
  useTheme,
  VStack,
  UserGroupBadge,
  HStack,
  TableHeaderCellContentPrefab,
  type TableHeaderCellContentPrefabProps,
  Tfoot,
  Flex,
  Spinner,
  Center,
} from '@frontend/components';

import {
  DefaultTableShadowOverlay,
  StickyElementRefs,
} from 'components/listTablePage';

import { formatPrice } from 'utils/formatters';
import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';
import { pluralize, toSnakeCase } from 'utils/stringUtils';

import {
  Pane,
  PaneBody,
  PaneHeader,
  PaneSubtitle,
  PaneTitle,
} from '../components/pane';
import {
  buildQuotesOverviewCellUrl,
  type OverviewColumnId,
  type OverviewEntity,
} from './buildQuotesOverviewCellUrl';

type OnCellClick = (
  columnId: OverviewColumnId,
  entity: OverviewEntity,
  userGroupId: string,
) => void;

const CellClickContext = createContext<OnCellClick | null>(null);

type DueCellContentProps = QuotesOverviewRow['dueThisWeek'] & {
  columnId?: OverviewColumnId;
  userGroupId?: string;
};

const DueCellContent = ({
  count,
  overdueCount,
  columnId,
  userGroupId,
}: DueCellContentProps) => {
  const onCellClick = useContext(CellClickContext);
  const handleQuoteClick =
    count > 0 && columnId && userGroupId && onCellClick
      ? () => onCellClick(columnId, VIEW_ENTITY_TYPES.QUOTES, userGroupId)
      : undefined;
  return (
    <VStack gap={0} align="end">
      {count === 0 ? (
        <Box color="feature.placeholder">--</Box>
      ) : handleQuoteClick ? (
        <Button variant="link" size="md" onClick={handleQuoteClick}>
          {pluralize('quote', count)}
        </Button>
      ) : (
        <Box>{pluralize('quote', count)}</Box>
      )}
      {overdueCount > 0 && (
        <Badge colorScheme="error" variant="outline" size="xs">
          {overdueCount} overdue
        </Badge>
      )}
    </VStack>
  );
};

type CountCellContentProps = QuotesOverviewRow['notStarted'] & {
  columnId?: OverviewColumnId;
  userGroupId?: string;
};

const CountCellContent = ({
  count,
  itemCount,
  columnId,
  userGroupId,
}: CountCellContentProps) => {
  const onCellClick = useContext(CellClickContext);
  const handleQuoteClick =
    count > 0 && columnId && userGroupId && onCellClick
      ? () => onCellClick(columnId, VIEW_ENTITY_TYPES.QUOTES, userGroupId)
      : undefined;
  const handleItemClick =
    itemCount > 0 && columnId && userGroupId && onCellClick
      ? () => onCellClick(columnId, VIEW_ENTITY_TYPES.QUOTE_ITEMS, userGroupId)
      : undefined;
  return (
    <VStack gap={0} align="end">
      {count === 0 ? (
        <Box color="feature.placeholder">--</Box>
      ) : handleQuoteClick ? (
        <Button variant="link" size="md" onClick={handleQuoteClick}>
          {pluralize('quote', count)}
        </Button>
      ) : (
        <Box>{pluralize('quote', count)}</Box>
      )}
      {itemCount > 0 &&
        (handleItemClick ? (
          <Button variant="link" size="sm" onClick={handleItemClick}>
            {pluralize('line item', itemCount)}
          </Button>
        ) : (
          <Box fontSize="sm" lineHeight="sm">
            {pluralize('line item', itemCount)}
          </Box>
        ))}
    </VStack>
  );
};

type PriceCellContentProps = QuotesOverviewRow['outstanding'] & {
  columnId?: OverviewColumnId;
  userGroupId?: string;
};

const PriceCellContent = ({
  count,
  totalPrice,
  columnId,
  userGroupId,
}: PriceCellContentProps) => {
  const onCellClick = useContext(CellClickContext);
  const handleQuoteClick =
    count > 0 && columnId && userGroupId && onCellClick
      ? () => onCellClick(columnId, VIEW_ENTITY_TYPES.QUOTES, userGroupId)
      : undefined;
  return (
    <VStack gap={0} align="end">
      {count === 0 ? (
        <Box color="feature.placeholder">--</Box>
      ) : handleQuoteClick ? (
        <Button variant="link" size="md" onClick={handleQuoteClick}>
          {pluralize('quote', count)}
        </Button>
      ) : (
        <Box>{pluralize('quote', count)}</Box>
      )}
      {totalPrice > 0 && (
        <Box fontFamily="data" fontSize="sm" color="feature.secondary">
          {formatPrice(totalPrice)}
        </Box>
      )}
    </VStack>
  );
};

// ---------------------------------------------------------------------------
// Table layout helpers
// ---------------------------------------------------------------------------

const QUOTES_OVERVIEW_COLUMN_MIN_WIDTHS: Record<string, number> = {
  userGroup: 23,
  dueThisWeek: 18,
  dueNextSevenDays: 18,
  notStarted: 18,
  onHold: 14,
  createdThisWeek: 21,
  createdLastSevenDays: 18,
  sentThisWeek: 18,
  sentLastSevenDays: 18,
  outstanding: 19,
};

const QUOTES_OVERVIEW_COLUMN_MAX_WIDTHS: Record<string, string> = {
  userGroup: 'max-content',
};

const useGridTemplateColumns = (columnIds: string[]): string => {
  const theme = useTheme();
  return useMemo(() => {
    return columnIds
      .map((id) => {
        const minUnits = QUOTES_OVERVIEW_COLUMN_MIN_WIDTHS[id] ?? 0;
        const maxWidth = QUOTES_OVERVIEW_COLUMN_MAX_WIDTHS[id] ?? '1fr';
        return `minmax(${theme.space[minUnits]}, ${maxWidth})`;
      })
      .join(' ');
  }, [columnIds, theme.space]);
};

const TableHeaderCellContent = ({
  column,
  table,
  ...restProps
}: {
  column: Column<QuotesOverviewRow>;
  table: Table<QuotesOverviewRow>;
} & Pick<TableHeaderCellContentPrefabProps, 'columnTitle'> &
  Partial<Omit<TableHeaderCellContentPrefabProps, 'columnTitle'>>) => {
  const sorting = table.getState().sorting;
  const current = sorting.find((s) => s.id === column.id);
  return (
    <TableHeaderCellContentPrefab
      columnDataIndex={column.id}
      sortPossibleDirections={['ascending', 'descending']}
      sortDirection={
        current ? (current.desc ? 'descending' : 'ascending') : undefined
      }
      onIncrementSort={() =>
        table.setSorting([{ id: column.id, desc: !current?.desc }])
      }
      {...restProps}
    />
  );
};

const EMPTY_QUOTES_OVERVIEW_GROUPS: QuotesOverviewRow[] = [];

const DEFAULT_TOTAL_DATA = {
  dueThisWeek: { count: 0, overdueCount: 0 },
  dueNextSevenDays: { count: 0, overdueCount: 0 },
  notStarted: { count: 0, itemCount: 0 },
  onHold: { count: 0, itemCount: 0 },
  createdThisWeek: { count: 0, itemCount: 0 },
  createdLastSevenDays: { count: 0, itemCount: 0 },
  sentThisWeek: { count: 0, itemCount: 0 },
  sentLastSevenDays: { count: 0, itemCount: 0 },
  outstanding: { count: 0, totalPrice: 0 },
} as const satisfies QuotesOverviewResponse['total'];

const TotalDataContext =
  createContext<QuotesOverviewResponse['total']>(DEFAULT_TOTAL_DATA);

const useTotalData = () => useContext(TotalDataContext);

const columns: ColumnDef<QuotesOverviewRow>[] = [
  {
    id: 'userGroup',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Site Name"
        column={column}
        table={table}
      />
    ),
    cell: ({ row }) => {
      const { name, icon, color } = row.original.userGroup;
      return (
        <UserGroupBadge
          name={name}
          icon={icon}
          colorPalette={color}
          size="md"
          maxWidth="max-content"
        />
      );
    },
    footer: () => (
      <Flex height="full" alignItems="center">
        Total
      </Flex>
    ),
  },
  {
    id: 'dueThisWeek',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Due this week"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <DueCellContent
        {...row.original.dueThisWeek}
        columnId="dueThisWeek"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <DueCellContent {...totalData?.dueThisWeek} />;
    },
  },
  {
    id: 'dueNextSevenDays',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Due next 7d"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <DueCellContent
        {...row.original.dueNextSevenDays}
        columnId="dueNextSevenDays"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <DueCellContent {...totalData?.dueNextSevenDays} />;
    },
  },
  {
    id: 'notStarted',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle={
          <HStack gap={0.25}>
            <Box flex={1}>Not Started</Box>
            <Tooltip label='Quotes and line items created 72+ hours ago with the status "Not Started"'>
              <OutlineCircleInfoIcon size={2.25} color="feature.secondary" />
            </Tooltip>
          </HStack>
        }
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        {...row.original.notStarted}
        columnId="notStarted"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent {...totalData?.notStarted} />;
    },
  },
  {
    id: 'onHold',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="On Hold"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        {...row.original.onHold}
        columnId="onHold"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent {...totalData?.onHold} />;
    },
  },
  {
    id: 'createdThisWeek',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Created this week"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        {...row.original.createdThisWeek}
        columnId="createdThisWeek"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent {...totalData?.createdThisWeek} />;
    },
  },
  {
    id: 'createdLastSevenDays',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Created last 7d"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        {...row.original.createdLastSevenDays}
        columnId="createdLastSevenDays"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent {...totalData?.createdLastSevenDays} />;
    },
  },
  {
    id: 'sentThisWeek',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Sent this week"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        {...row.original.sentThisWeek}
        columnId="sentThisWeek"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent {...totalData?.sentThisWeek} />;
    },
  },
  {
    id: 'sentLastSevenDays',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Sent last 7d"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        {...row.original.sentLastSevenDays}
        columnId="sentLastSevenDays"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent {...totalData?.sentLastSevenDays} />;
    },
  },
  {
    id: 'outstanding',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle={
          <HStack gap={0.25}>
            <Box flex={1}>Outstanding</Box>
            <Tooltip label="Dollar values are the sum of maximum possible quote values">
              <OutlineCircleInfoIcon size={2.25} color="feature.secondary" />
            </Tooltip>
          </HStack>
        }
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <PriceCellContent
        {...row.original.outstanding}
        columnId="outstanding"
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <PriceCellContent {...totalData?.outstanding} />;
    },
  },
];

type QuotesOverviewTableProps = {
  /** Optional preloaded data (e.g. from stories); live data comes from the hook. */
  data?: QuotesOverviewResponse;
  activeGroupIds: number[];
};

const subgridSx = {
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: '1 / -1',
} as const;

const QuotesOverviewTable = ({
  data: dataProp,
  activeGroupIds,
}: QuotesOverviewTableProps) => {
  const { initChangeSite } = useUserGroupCollectionContext();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'userGroup', desc: false },
  ]);

  const handleCellClick = useCallback(
    (
      columnId: OverviewColumnId,
      entity: OverviewEntity,
      userGroupId: string,
    ) => {
      initChangeSite({
        nextUrl: buildQuotesOverviewCellUrl(columnId, entity),
        userGroupId: Number(userGroupId),
      });
    },
    [initChangeSite],
  );

  const query = useMemo(
    () => ({
      o: `${sorting
        .map((s) => `${s.desc ? '-' : ''}${toSnakeCase(s.id)}`)
        .join(',')}`,
      ...(activeGroupIds.length > 0 ? { groups: activeGroupIds } : {}),
    }),
    [sorting, activeGroupIds],
  );

  const quotesQuery = useGetQuotesOverview({
    query,
    queryOptions: {
      useErrorBoundary: true,
    },
  });

  const data = quotesQuery.data ?? dataProp;

  const table = useReactTable<QuotesOverviewRow>({
    data: data?.groups ?? EMPTY_QUOTES_OVERVIEW_GROUPS,
    columns,
    getRowId: (row) => row.userGroup.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const overflowBoxRef = useRef<HTMLDivElement>(null);
  const topLeftStickyRef = useRef<HTMLTableCellElement>(null);
  const bottomLeftStickyRef = useRef<HTMLTableCellElement>(null);

  const stickyElementRefs = useMemo(() => {
    return {
      top: topLeftStickyRef,
      left: topLeftStickyRef,
      bottom: bottomLeftStickyRef,
    } satisfies StickyElementRefs;
  }, [topLeftStickyRef, bottomLeftStickyRef]);

  const { rows } = table.getRowModel();

  const headerGroups = table.getHeaderGroups();
  const gridTemplateColumns = useGridTemplateColumns(
    headerGroups[0].headers.map((h) => h.column.id),
  );

  return (
    <CellClickContext.Provider value={handleCellClick}>
      <TableContainer
        border="none"
        borderRadius="none"
        position="relative"
        variant="simple"
        minHeight="full"
      >
        {quotesQuery.isFetching ? (
          <Center
            as="span"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex="overlay"
            bg="page.background"
            opacity={0.5}
          >
            <Spinner size="xl" />
          </Center>
        ) : null}
        <DefaultTableShadowOverlay
          borderRadius="inherit"
          overflowBoxRef={overflowBoxRef}
          stickyElementRefs={stickyElementRefs}
          enableStickyBorders
        />
        <Box ref={overflowBoxRef} w="100%" flexGrow={1} overflow="auto">
          <TableV2
            sx={{
              display: 'grid',
              gridTemplateColumns,
              gridTemplateRows: 'auto auto minmax(0, 1fr) auto', // one "row" per sub grid
              minHeight: '100%',
              '--row-hover-bg': 'var(--chakra-colors-page-background)',
              '--row-active-bg': 'var(--chakra-colors-page-background)',
            }}
          >
            <Thead sx={subgridSx}>
              {headerGroups.map((headerGroup) => (
                <Tr key={headerGroup.id} sx={subgridSx}>
                  {headerGroup.headers.map((header, i) => (
                    <Th
                      key={header.id}
                      ref={i === 0 ? topLeftStickyRef : undefined}
                      {...(i === 0 && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                      })}
                      textAlign="right"
                      padding="0 !important" // to support the prefab which has hardcoded padding
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody sx={subgridSx}>
              {rows.map((row) => (
                <Tr key={row.id} sx={subgridSx} isInteractive cursor="default">
                  {row.getVisibleCells().map((cell, i) => (
                    <Td
                      key={cell.id}
                      ref={i === 0 ? bottomLeftStickyRef : undefined}
                      {...(i === 0 && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                      })}
                      py={0}
                      height={7}
                      fontWeight="medium"
                      outerProps={{
                        padding: 0,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
            <Tfoot sx={subgridSx} />
            <TotalDataContext.Provider
              value={data?.total ?? DEFAULT_TOTAL_DATA}
            >
              <Tfoot sx={subgridSx}>
                {table.getFooterGroups().map((footerGroup) => (
                  <Tr key={footerGroup.id} sx={subgridSx}>
                    {footerGroup.headers.map((header, i) => (
                      <Th
                        key={header.id}
                        height={7}
                        border="none"
                        {...(i === 0 && {
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                        })}
                      >
                        {flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Tfoot>
            </TotalDataContext.Provider>
          </TableV2>
        </Box>
      </TableContainer>
    </CellClickContext.Provider>
  );
};

const websocketOptions = {
  topic: ['multisite.quotes.created'], // this topic does not exist, we don't have any ws topics for this yet
};

export type QuotesOverviewPaneProps = {
  data?: QuotesOverviewResponse;
  activeGroupIds: number[];
};

export const QuotesOverviewPane = (props: QuotesOverviewPaneProps) => {
  return (
    <Pane
      refetchQueryKey={getQuotesOverviewQueryKeyPrefix()}
      websocketOptions={websocketOptions}
    >
      <PaneHeader>
        <PaneTitle>Quotes Overview</PaneTitle>
        <PaneSubtitle>Created in the last 90 days</PaneSubtitle>
      </PaneHeader>
      <PaneBody>
        <QuotesOverviewTable {...props} />
      </PaneBody>
    </Pane>
  );
};
